import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Gift, Heart, CheckCircle, Users, Sparkles, 
  Clock, AlertCircle, Loader2, ArrowRight
} from 'lucide-react';

const API_URL = window.location.origin;

const AcceptGiftPage = () => {
  const { giftCode } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Automatically redirect to register page with gift code
    // This page exists as a fallback for old links
    if (giftCode) {
      navigate(`/register?gift=${giftCode}`, { replace: true });
    }
  }, [giftCode, navigate]);

  const validateGiftCode = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/validate/${giftCode}`);
      setGift(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired gift code');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptGift = () => {
    // Store gift code in sessionStorage for registration flow
    sessionStorage.setItem('gift_code', giftCode);
    sessionStorage.setItem('gift_email', gift.recipient_email);
    sessionStorage.setItem('gift_name', gift.recipient_name);
    
    // Navigate to registration with gift parameters
    navigate(`/register?gift=${giftCode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-forest mx-auto mb-4" />
          <p className="text-gray-600">Validating your gift...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="font-heading text-xl text-forest mb-2">Gift Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="btn-primary">
                Visit Sister Diamonds
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-cream to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Gift Card */}
        <Card className="overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-6 text-white text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-white/20 mb-4">
              <Gift className="h-8 w-8" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
              You've Been Gifted!
            </h1>
            <p className="text-pink-100">
              A special invitation from someone who believes in you
            </p>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* From Section */}
            <div className="text-center">
              <Badge className="bg-pink-100 text-pink-700 mb-3">
                <Heart className="h-3 w-3 mr-1" /> Gift from
              </Badge>
              <p className="text-2xl font-heading font-bold text-forest">
                {gift.gifter_name}
              </p>
            </div>

            {/* Personal Message */}
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-5 rounded-xl border border-pink-200">
              <p className="text-sm text-pink-600 font-medium mb-2">A message for you:</p>
              <p className="text-gray-700 italic leading-relaxed">
                "{gift.personal_message}"
              </p>
            </div>

            {/* Why They're Gifting */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 font-medium mb-1">
                Why {gift.gifter_name} chose this gift:
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {gift.why_gifting}
              </p>
            </div>

            {/* What You'll Get */}
            <div>
              <h3 className="font-heading text-lg text-forest mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-terracotta" />
                Your Gift Includes:
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>30 days FREE</strong> in the Sister Diamonds Inner Circle</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access to sacred sister circles and prayer spaces</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Weekly transmissions and reflection prompts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Connection with high-capacity women who understand</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>AI coaching assistant (SHE) for guidance</span>
                </li>
              </ul>
            </div>

            {/* Expiration Notice */}
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
              <Clock className="h-4 w-4" />
              <span>
                This gift expires on {new Date(gift.expires_at).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
            </div>

            {/* Accept Button */}
            <Button 
              onClick={handleAcceptGift}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg"
              data-testid="accept-gift-btn"
            >
              Accept Your Gift
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>

            <p className="text-xs text-center text-gray-500">
              No credit card required. Cancel anytime.
            </p>
          </CardContent>
        </Card>

        {/* Sister Diamonds Branding */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            A gift from the <span className="font-semibold text-forest">Sister Diamonds</span> community
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptGiftPage;
